import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState, type SyntheticEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { isAppError } from '@/shared/api/errors';
import { useSession, useSignIn } from '@/shared/session/useSession';
import { BrandMark } from '../layout/BrandMark';
import { safeNext } from './safeNext';

function messageFor(error: unknown): string {
  if (isAppError(error) && error.status === 429) return 'Too many attempts. Wait a few minutes and try again.';
  if (isAppError(error) && error.status === 401) return 'Email or password is incorrect.';
  if (isAppError(error) && error.status === 503) return 'Sign-in is not set up on the server yet. Ask your administrator.';
  return 'Could not sign in. Check your connection and try again.';
}

export function LoginPage() {
  const session = useSession();
  const signIn = useSignIn();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (session.data) return <Navigate to={safeNext(params.get('next'))} replace />;

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    signIn.mutate({ email: email.trim(), password });
  };

  return (
    <Box component="main" sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', px: 2, bgcolor: 'background.default' }}>
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 400, p: { xs: 3, sm: 4 } }}>
        <Stack component="form" onSubmit={submit} noValidate sx={{ gap: 2.5 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
            <BrandMark />
            <Stack>
              <Typography variant="h4" component="p" sx={{ lineHeight: 1.2 }}>Research Assistant</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>OCBC Global Markets</Typography>
            </Stack>
          </Stack>
          <Typography variant="h2" component="h1">Sign in</Typography>
          {signIn.isError ? <Alert severity="error">{messageFor(signIn.error)}</Alert> : null}
          <TextField
            label="Work email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => { setEmail(e.target.value); }}
            required
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); }}
            required
          />
          <Button type="submit" variant="contained" size="large" loading={signIn.isPending} disabled={!email.trim() || !password}>
            Sign in
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
